import { editorGuidKeyName } from "@needle-tools/engine";
import { Emitter, Event } from "../../../base/common/events";
import { Disposable } from "../../../base/common/lifecycle";
import { EditorWindow, IEditorWindow } from "../../window/browser/window";
import { IOpenConfiguration, IWindowsMainService } from "../common/windows";
import { IAnalyticsService } from "../../analytics/common/analytics";
import { getWindowId, registerWindow } from "../../../base/browser/dom";
import { ensureCodeWindow } from "../../../base/browser/window";
import { ILogService } from "../../log/common/log";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage";
import { INotificationService } from "../../notifications/common/notifications";
import { Delayer } from "../../../base/common/async";
import { createPopoutToken, restoreExtensionPopout } from './extensionPopout.js';

let _windowIds = 0;

export class WindowsMainService extends Disposable implements IWindowsMainService {
    _serviceBrand: undefined;

    readonly allowPopout: boolean = false;

    private readonly _onDidOpenWindow = this._register(new Emitter<IEditorWindow>());
    readonly onDidOpenWindow: Event<IEditorWindow> = this._onDidOpenWindow.event;

    private readonly _onDidDestroyWindow = this._register(new Emitter<IEditorWindow>());
    readonly onDidDestroyWindow: Event<IEditorWindow> = this._onDidDestroyWindow.event;

    private mainWindow: IEditorWindow | null = null;
    private windows: IEditorWindow[] = [];

    constructor(
        private readonly config?: { allowPopout: boolean; useExtensionBridge?: boolean },
        @IAnalyticsService telemetry?: IAnalyticsService,
        @ILogService private readonly logger?: ILogService,
        @IStorageService private readonly storageService?: IStorageService,
        @INotificationService private readonly notificationService?: INotificationService,
    ) {
        super();
        this.allowPopout = config?.allowPopout ?? false;
        ensureCodeWindow(window, 0);
        this.mainWindow = new EditorWindow(globalThis.window, {
            id: String(window.needleWindowId),
            view: "main",
        });
        this.windows.push(this.mainWindow);

        this.mainWindow.onDidClose(() => {
            for (const win of this.windows) {
                if (win !== this.mainWindow) {
                    win.getWindow().close();
                }
            }
            this.windows = [];
        });
    }

    async open(openConfig: IOpenConfiguration): Promise<IEditorWindow[]> {

        const id = openConfig.id;

        if (!this.config?.useExtensionBridge && openConfig.restorePreviousState && id) {
            const state = this.loadWindowState(id);
            if (state) {
                openConfig.expectedPosition = { x: state.x, y: state.y };
                openConfig.expectedSize = { width: state.innerWidth, height: state.innerHeight };
            }
        }

        const width = openConfig.expectedSize?.width ?? 1000;
        const height = openConfig.expectedSize?.height ?? 800;
        const popoutToken = this.config?.useExtensionBridge && !openConfig.url ? createPopoutToken() : undefined;
        const url = popoutToken ? `about:blank#needle-inspector-${popoutToken}` : openConfig.url ?? "";
        const positionX = openConfig.expectedPosition?.x ?? (globalThis.screen.width - width) / 2;
        const positionY = openConfig.expectedPosition?.y ?? (globalThis.screen.height - height) / 2;
        this.logger?.info(`Opening new window`, openConfig);
        const newWindow = globalThis.open(url, "_blank", `left=${positionX},top=${positionY},width=${width},height=${height},resizable,scrollbars,titlebar=no,menubar=no,toolbar=no,location=no,status=no`);
        if (!newWindow) {
            this.notificationService?.error("Failed to open new window (popup blocked?)");
            throw new Error("Failed to open new window (popup blocked?)");
        }
        if (popoutToken) {
            try {
                await restoreExtensionPopout(newWindow, popoutToken, !!openConfig.restorePreviousState);
            } catch (error) {
                newWindow.close();
                throw error;
            }
        }
        newWindow.document.title = openConfig.title || (window.document.title + ' — Needle Inspector');

        const editorWindow = new EditorWindow(
            newWindow, {
            id: id || `window-${++_windowIds}`,
            view: openConfig.view || 'default',
        });
        let closing = false;
        editorWindow.onDidClose(() => {
            closing = true;
            const index = this.windows.indexOf(editorWindow);
            if (index >= 0) {
                this.windows.splice(index, 1);
            }
            this._onDidDestroyWindow.fire(editorWindow);
        });

        if (id && !popoutToken) {
            const delayer = new Delayer(100);
            const requestSaveState = () => {
                delayer.trigger(() => {
                    if (!closing) this.saveWindowState(id, newWindow);
                });
            }
            editorWindow.onDidResize(() => requestSaveState());
            editorWindow.onDidMove(() => requestSaveState());
        }

        this.windows.push(editorWindow);
        this._onDidOpenWindow.fire(editorWindow);

        return [editorWindow];
    }

    getWindows(): IEditorWindow[] {
        return this.windows;
    }

    async captureWindowState(id: string, window: Window): Promise<void> {
        if (this.config?.useExtensionBridge) return;
        if (this.saveWindowState(id, window)) await this.storageService?.flush();
    }


    private loadWindowState(id: string): WindowStateModel | null {
        this.logger?.info(`Restoring window state for id: ${id}`);
        const storedState = this.storageService?.getObject<WindowStateModel>(`window-state-${id}`, StorageScope.PROFILE);
        if (storedState) {
            // Windows can report this off-screen position while tearing down a minimized popup.
            if ((storedState.x <= -30_000 && storedState.y <= -30_000)
                || !Number.isFinite(storedState.x) || !Number.isFinite(storedState.y)
                || !Number.isFinite(storedState.innerWidth) || !Number.isFinite(storedState.innerHeight)
                || storedState.innerWidth <= 0 || storedState.innerHeight <= 0) return null;
            return storedState;
        }
        return null;
    }

    private saveWindowState(id: string, window: Window): boolean {
        const state: WindowStateModel = {
            width: window.outerWidth,
            height: window.outerHeight,
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            x: window.screenX,
            y: window.screenY
        };
        if (!Number.isFinite(state.x) || !Number.isFinite(state.y)
            || !Number.isFinite(state.width) || !Number.isFinite(state.height)
            || state.width <= 0 || state.height <= 0) return false;
        const display = window.screen as Screen & { availLeft?: number; availTop?: number };
        if (display && Number.isFinite(display.availLeft) && Number.isFinite(display.availTop)
            && Number.isFinite(display.availWidth) && Number.isFinite(display.availHeight)
            && (state.x + state.width <= display.availLeft! || state.x >= display.availLeft! + display.availWidth
                || state.y + state.height <= display.availTop! || state.y >= display.availTop! + display.availHeight)) {
            return false;
        }
        this.logger?.info(`Saving window state for id: ${id}`);
        this.storageService?.store(`window-state-${id}`, state, StorageScope.PROFILE, StorageTarget.MACHINE);
        return true;
    }

}

interface WindowStateModel {
    /** Outer width of the window */
    width: number;
    /** Outer height of the window */
    height: number;
    innerWidth: number;
    innerHeight: number;
    x: number;
    y: number;
}
